import React, { useState, useRef, useEffect } from 'react';

const FONT_FAMILY =
  "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif";

// Color constants
const MAIN_BG = "#1a1a1a";
const ACCENT = "#ffffff";
const BORDER = "#333333";
const SUBTLE = "#444444";
const LINK = "#4A90E2";

// Example projects (can be expanded)
const projects = [
  {
    id: "portfolio-website",
    name: "Personal Portfolio Website",
    desc: "A sophisticated, interactive portfolio built with React featuring WebGL shader backgrounds and smooth animations.",
    tag: "design",
    url: null,
    details: {
      overview: "A modern, minimalist portfolio website showcasing creative work and professional experience. Built with React and featuring custom WebGL shader backgrounds that create dynamic, interactive ripple effects. The design emphasizes clean typography, smooth animations, and an elegant user experience.",
      technologies: ["React", "JavaScript", "WebGL", "CSS3", "HTML5"],
      features: ["Interactive WebGL Backgrounds", "Smooth Page Transitions", "Responsive Design", "Custom Shader Effects", "Project Detail Views", "Accessible Navigation"],
      link: null
    }
  },
  {
    id: "warehouse-system",
    name: "Warehouse Inventory Management System",
    desc: "Built an automated inventory and fulfillment system that outperformed their previous 3PL with minimal staff.",
    tag: "operations",
    url: "https://github.com/mushyyams/warehouse-manager",
    details: {
      overview: "Designed and implemented a comprehensive warehouse management system for a business transitioning from 3PL to in-house operations. The system automated inventory tracking, order fulfillment, and shipment processing using a custom tech stack that integrated multiple APIs and automation tools. This solution reduced operational costs by 40% while improving order accuracy to 99.8% and reducing fulfillment time by 60%.",
      technologies: ["Google Sheets", "Shopify API", "Tracktry API", "UPS API", "Google Apps Script", "Postman", "Jira"],
      features: ["Automated Inventory Tracking", "Order Fulfillment Automation", "Shipment Tracking Integration", "Returns Processing", "Bulk Operations", "Performance Analytics", "Error Reduction"],
      screenshots: [
        {
          title: "Dashboard Overview",
          description: "Real-time warehouse operations dashboard showing inventory levels, upcoming shipments, and app performance metrics.",
          url: require("./images/Screenshot_Dashboard.png")
        },
        {
          title: "Visual Map",
          description: "Interactive visual map showing warehouse layout and product locations for efficient navigation.",
          url: require("./images/screenshot_visual map.png")
        },
        {
          title: "Product Inventory",
          description: "Comprehensive product inventory management with real-time stock levels and reorder alerts.",
          url: require("./images/screenshot_visualmap_inventory.png")
        },
        {
          title: "Item Location Editor",
          description: "Advanced location editing system for precise product placement and warehouse optimization.",
          url: require("./images/screenshot_visualmap_locationedit.png")
        },
        {
          title: "Tracking",
          description: "Real-time shipment tracking and order status monitoring across multiple carriers.",
          url: require("./images/Screenshot_tracking.png")
        },
        {
          title: "Returns",
          description: "Automated returns processing system with gift card issuing, restocking inventory, and streamlined workflows.",
          url: require("./images/screenshot_returns.png")
        }
      ],
      businessImpact: [
        "40% reduction in operational costs",
        "99.8% order accuracy rate",
        "60% faster order fulfillment",
        "Eliminated 3PL dependency",
        "Scalable to handle 500+ orders/day"
      ],
             technicalHighlights: [
         "Multi-API integration (Shopify, UPS, Tracktry)",
         "Real-time inventory synchronization",
         "Automated workflow orchestration",
         "Custom reporting and analytics",
         "Error handling and recovery systems",
         "React.js frontend development",
         "Cursor IDE for enhanced development workflow"
       ],
      link: "https://github.com/mushyyams/warehouse-manager"
    }
  },
  {
    id: "yams-drops",
    name: "Yam Drops",
    desc: "A passion project connecting culinary creativity with community support through experimental food projects.",
    tag: "culinary",
    url: "https://yamdrops.carrd.co/",
    details: {
      overview: "Yams Drops is a community-driven culinary passion project that bridges creative experimentation with supporter engagement. Supporters can invest in ongoing food projects and receive finished products in return, creating a unique ecosystem where culinary creativity meets community support. The project aims to build a sustainable community around food innovation and personal culinary growth.",
      skillsAndMethods: ["Community Building", "Culinary Arts", "Project Management", "Social Media", "E-commerce"],
              highlights: ["Crowdfunding Model", "Experimental Food Projects", "Community Engagement", "Product Delivery", "Creative Collaboration", "Sustainable Growth"],
      link: "https://yamsdrops.com"
    }
  },
  {
    id: "three-neighbors",
    name: "Three Neighbors",
    desc: "A collaborative pizza popup group hosting intimate 8-person prix-fixe meals with cost optimization research.",
    tag: "culinary",
    url: null,
    details: {
      overview: "Three Neighbors was a collaborative pizza popup group that created intimate dining experiences through monthly 8-person prix-fixe meals. Beyond the culinary execution, the project involved extensive research into cost estimation and optimization strategies to maintain quality while reducing operational expenses. This collaborative effort combined creative cooking with strategic business thinking.",
      skillsAndMethods: ["Culinary Arts", "Cost Analysis", "Event Planning", "Collaborative Cooking", "Menu Development"],
              highlights: ["Intimate Dining Experiences", "Prix-Fixe Menu Design", "Cost Optimization Research", "Monthly Event Planning", "Quality Control", "Collaborative Operations"],
      link: null
    }
  }
];

// 0.5in = 48px (approx)
const BORDER_MARGIN = "48px";

// WebGL helper functions
function createShader(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader));
    gl.deleteShader(shader);
    return null;
  }
  
  return shader;
}

function createProgram(gl, vertexShader, fragmentShader) {
  const program = gl.createProgram();
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(program));
    gl.deleteProgram(program);
    return null;
  }
  
  return program;
}

// Shader Background Component
function ShaderBackground() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const clickRippleRef = useRef({ active: false, x: 0.0, y: 0.0, time: 0 });
  // Add ref for content area
  const contentRef = useRef(null);

  useEffect(() => {
    console.log('=== WEBGL SHADER START ===');
    console.log('useEffect is running!');
    
    const canvas = canvasRef.current;
    console.log('Canvas element:', canvas);
    
    if (!canvas) {
      console.error('Canvas is null!');
      return;
    }
    
    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    console.log('Canvas size set to:', canvas.width, 'x', canvas.height);
    
    const gl = canvas.getContext('webgl');
    console.log('WebGL context:', gl);
    
    if (!gl) {
      console.log('WebGL not supported, using 2D fallback');
      // Fallback to 2D canvas
      const ctx = canvas.getContext('2d');
      if (ctx) {
        function draw2DFallback() {
          const time = Date.now() * 0.001;
          
          // Clear with fade effect
          ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          // Draw animated light circles
          for (let i = 0; i < 6; i++) {
            const x = canvas.width * 0.5 + Math.sin(time * 0.5 + i * 0.8) * 300;
            const y = canvas.height * 0.5 + Math.cos(time * 0.3 + i * 0.6) * 300;
            const radius = 60 + Math.sin(time * 2 + i) * 30;
            
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.fillStyle = `hsl(${240 + i * 30 + time * 50}, 70%, 60%)`; // Animated colors
            ctx.fill();
          }
          
          requestAnimationFrame(draw2DFallback);
        }
        
        console.log('Starting 2D fallback animation');
        draw2DFallback();
      }
      return;
    }

    // Vertex shader
    const vertexShaderSource = `
      attribute vec2 a_position;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;

    // Fragment shader - BLACK SCREEN WITH THIN TRAVELING RIPPLES
    const fragmentShaderSource = `
      precision mediump float;
      uniform vec2 u_resolution;
      uniform float u_time;
      uniform vec2 u_mouse;
      uniform vec2 u_clickRipple;
      uniform float u_clickTime;
      uniform vec2 u_contentMin;
      uniform vec2 u_contentMax;

      // Simple random noise function
      float random(vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
      }

      // Wii-style ripple function - fluid and dynamic
      float wiiRipple(vec2 p, vec2 startPos, float speed, float frequency, float delay) {
        float dist = distance(p, startPos);
        float waveRadius = (u_time - u_clickTime - delay) * speed;
        
        // Create multiple wave layers for fluid effect
        float wave1 = sin((dist - waveRadius) * frequency);
        float wave2 = sin((dist - waveRadius * 0.8) * frequency * 1.2);
        float wave3 = sin((dist - waveRadius * 1.2) * frequency * 0.8);
        
        // Combine waves for fluid motion
        float combinedWave = (wave1 + wave2 * 0.5 + wave3 * 0.3) / 1.8;
        
        // Make the ripple thinner and smaller
        float rippleThickness = 0.012;
        float ripple = smoothstep(-rippleThickness, rippleThickness, combinedWave);
        // Limit the ripple to a smaller max radius
        float maxRadius = 0.18;
        float showRipple = step(dist, maxRadius) * step(waveRadius, dist + 1.2);
        
        // Quick fade out for distinct rings
        float fadeOut = 1.0 - smoothstep(0.0, maxRadius * 0.4, waveRadius);
        fadeOut *= 1.0 + 0.1 * sin((u_time - u_clickTime - delay) * 6.0) * exp(-(u_time - u_clickTime - delay) * 4.0);
        
        return (ripple) * showRipple * fadeOut;
      }

      void main() {
        vec2 uv = gl_FragCoord.xy / u_resolution.xy;
        
        // Use u_contentMin/u_contentMax for the true content area
        bool insideContent = uv.x >= u_contentMin.x && uv.x <= u_contentMax.x &&
                        uv.y >= u_contentMin.y && uv.y <= u_contentMax.y;
        
        if (insideContent) {
          // Remap UV to content area coordinates (0-1)
          vec2 contentUV = (uv - u_contentMin) / (u_contentMax - u_contentMin);
          // CRT bulge distortion
          vec2 mouseContent = (u_mouse - u_contentMin) / (u_contentMax - u_contentMin);
          float distToMouse = distance(contentUV, mouseContent);
          float bulgeStrength = 0.18;
          vec2 bulgeOffset = (contentUV - mouseContent) * bulgeStrength * exp(-pow(distToMouse * 5.0, 2.0));
          vec2 distortedUV = clamp(contentUV + bulgeOffset, 0.0, 1.0);
          vec2 rippleOffset = vec2(0.0);
          float grainStrength = 0.22;
          // Ripple logic: compute offset if active
          if (u_clickRipple.x > 0.0 && u_clickRipple.y > 0.0) {
            vec2 scaledClick = (u_clickRipple - u_contentMin) / (u_contentMax - u_contentMin);
            vec2 p = distortedUV * 15.0;
            float ripple1 = wiiRipple(p, scaledClick, 0.5, 15.0, 0.0);
            float ripple2 = wiiRipple(p, scaledClick, 0.5, 15.0, 0.4);
            float ripple3 = wiiRipple(p, scaledClick, 0.5, 15.0, 0.8);
            float combinedRipples = (ripple1 + ripple2 * 0.6 + ripple3 * 0.3) * 0.5;
            float fadeTime = clamp((u_time - u_clickTime) / 1.8, 0.0, 1.0);
            float fadeOut = 1.0 - fadeTime;
            float distToRipple = distance(contentUV, scaledClick);
            float rippleRadius = 1.0;
            if (fadeOut > 0.0) {
              rippleOffset = normalize(contentUV - scaledClick) * combinedRipples * fadeOut * 0.12;
              if (distToRipple < 0.005) rippleOffset = vec2(0.0);
            }
          }
          // Final UVs for speckle
          vec2 finalUV = clamp(distortedUV + rippleOffset, 0.0, 1.0);
          float finalGrain = random(finalUV * 120.0 + u_time * 10.0);
          vec3 baseColor = vec3(0.11, 0.11, 0.11);
          vec3 speckled = mix(baseColor, baseColor + grainStrength * (finalGrain - 0.5), 0.7);
          vec3 color = speckled;
          // Add white overlay if ripple is active
          if (u_clickRipple.x > 0.0 && u_clickRipple.y > 0.0) {
            vec2 scaledClick = (u_clickRipple - u_contentMin) / (u_contentMax - u_contentMin);
            vec2 p = distortedUV * 15.0;
            float ripple1 = wiiRipple(p, scaledClick, 0.5, 15.0, 0.0);
            float ripple2 = wiiRipple(p, scaledClick, 0.5, 15.0, 0.4);
            float ripple3 = wiiRipple(p, scaledClick, 0.5, 15.0, 0.8);
            float combinedRipples = (ripple1 + ripple2 * 0.6 + ripple3 * 0.3) * 0.5;
            float fadeTime = clamp((u_time - u_clickTime) / 1.8, 0.0, 1.0);
            float fadeOut = 1.0 - fadeTime;
            float distToRipple = distance(contentUV, scaledClick);
            float rippleRadius = 1.0;
            if (fadeOut > 0.0) {
              color += vec3(1.0) * (combinedRipples * fadeOut * 1.0);
            }
          }
          gl_FragColor = vec4(color, 1.0);
        } else {
          gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
        }
      }
    `;

    // Create shaders
    const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
    console.log('Shaders created:', { vertexShader, fragmentShader });
    
    // Check if shaders were created successfully
    if (!vertexShader || !fragmentShader) {
      console.error('Failed to create shaders, using 2D fallback');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        function draw2DFallback() {
          const time = Date.now() * 0.001;
          
          ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          for (let i = 0; i < 6; i++) {
            const x = canvas.width * 0.5 + Math.sin(time * 0.5 + i * 0.8) * 300;
            const y = canvas.height * 0.5 + Math.cos(time * 0.3 + i * 0.6) * 300;
            const radius = 60 + Math.sin(time * 2 + i) * 30;
            
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.fillStyle = `hsl(${240 + i * 30 + time * 50}, 70%, 60%)`;
            ctx.fill();
          }
          
          requestAnimationFrame(draw2DFallback);
        }
        
        console.log('Starting 2D fallback animation');
        draw2DFallback();
      }
      return;
    }
    
    // Create program
    const program = createProgram(gl, vertexShader, fragmentShader);
    console.log('Program created:', program);
    
    // Check if program was created successfully
    if (!program) {
      console.error('Failed to create program, using 2D fallback');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        function draw2DFallback() {
          const time = Date.now() * 0.001;
          
          ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          for (let i = 0; i < 6; i++) {
            const x = canvas.width * 0.5 + Math.sin(time * 0.5 + i * 0.8) * 300;
            const y = canvas.height * 0.5 + Math.cos(time * 0.3 + i * 0.6) * 300;
            const radius = 60 + Math.sin(time * 2 + i) * 30;
            
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            ctx.fillStyle = `hsl(${240 + i * 30 + time * 50}, 70%, 60%)`;
            ctx.fill();
          }
          
          requestAnimationFrame(draw2DFallback);
        }
        
        console.log('Starting 2D fallback animation');
        draw2DFallback();
      }
      return;
    }
    
    // Get attribute and uniform locations
    const positionLocation = gl.getAttribLocation(program, 'a_position');
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
    const timeLocation = gl.getUniformLocation(program, 'u_time');
    const mouseLocation = gl.getUniformLocation(program, 'u_mouse');
    const clickRippleLocation = gl.getUniformLocation(program, 'u_clickRipple');
    const clickTimeLocation = gl.getUniformLocation(program, 'u_clickTime');
    const contentMinLocation = gl.getUniformLocation(program, 'u_contentMin');
    const contentMaxLocation = gl.getUniformLocation(program, 'u_contentMax');
    console.log('Locations:', { positionLocation, resolutionLocation, timeLocation, mouseLocation, clickRippleLocation, clickTimeLocation, contentMinLocation, contentMaxLocation });
    
    // Create buffer
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
       1,  1,
    ]), gl.STATIC_DRAW);
    
    // Mouse event handlers
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = (e.clientX - rect.left) / rect.width;
      mouseRef.current.y = 1.0 - (e.clientY - rect.top) / rect.height;
      
      // Debug: Check if mouse is inside inner square
      const borderSize = 0.1;
      const innerMin = borderSize;
      const innerMax = 1.0 - borderSize;
      const insideInner = mouseRef.current.x >= innerMin && mouseRef.current.x <= innerMax && 
                         mouseRef.current.y >= innerMin && mouseRef.current.y <= innerMax;
      
      console.log('Mouse moved to:', mouseRef.current.x, mouseRef.current.y, 'Inside inner square:', insideInner);
    };
    
    canvas.addEventListener('mousemove', handleMouseMove);
    console.log('Mouse event listener added');
    
    // Get content area bounds
    function getContentBounds() {
      const canvas = canvasRef.current;
      const content = document.querySelector('[data-content-area]');
      if (!canvas || !content) return { min: [0.15, 0.15], max: [0.85, 0.85] };
      const canvasRect = canvas.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      // Calculate normalized min/max (0-1)
      const minX = (contentRect.left - canvasRect.left) / canvasRect.width;
      const minY = (contentRect.top - canvasRect.top) / canvasRect.height;
      const maxX = (contentRect.right - canvasRect.left) / canvasRect.width;
      const maxY = (contentRect.bottom - canvasRect.top) / canvasRect.height;
      return { min: [minX, 1.0 - maxY], max: [maxX, 1.0 - minY] };
    }

    // Animation loop
    let startTime = Date.now();
    function render() {
      const time = (Date.now() - startTime) * 0.001;
      
      // Set canvas size
      const displayWidth = canvas.clientWidth;
      const displayHeight = canvas.clientHeight;
      if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
        canvas.width = displayWidth;
        canvas.height = displayHeight;
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
        console.log('Canvas resized:', displayWidth, displayHeight);
      }
      
      // Clear
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      
      // Use program
      gl.useProgram(program);
      
      // Set uniforms
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform1f(timeLocation, time);
      gl.uniform2f(mouseLocation, mouseRef.current.x, mouseRef.current.y);
      gl.uniform2f(clickRippleLocation, clickRippleRef.current.x, clickRippleRef.current.y);
      gl.uniform1f(clickTimeLocation, clickRippleRef.current.time);
      // Set content area uniforms
      const { min, max } = getContentBounds();
      gl.uniform2f(contentMinLocation, min[0], min[1]);
      gl.uniform2f(contentMaxLocation, max[0], max[1]);
      
      // Set attributes
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);
      
      // Draw
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      
      requestAnimationFrame(render);
    }
    
    console.log('Starting render loop');
    render();
    
    // Expose ripple trigger function globally
    window.triggerRipple = (x, y) => {
      clickRippleRef.current = {
        active: true,
        x: x,
        y: y,
        time: (Date.now() - startTime) * 0.001
      };
      console.log('Ripple triggered at:', x, y);
    };
    
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      delete window.triggerRipple;
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        opacity: 1.0,
        pointerEvents: 'auto', // Enable mouse events
      }}
    />
  );
}

function App() {
  const [activeSection, setActiveSection] = useState("home");
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectFilters, setShowProjectFilters] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  
  // Get unique tags from projects
  const allTags = [...new Set(projects.map(project => project.tag))];

  // For bulging effect
  const scrollRef = useRef(null);
  const [cardScales, setCardScales] = useState([]);
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);

  // Update card scales on scroll
  useEffect(() => {
    function handleScroll() {
      const container = scrollRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      const cards = Array.from(container.querySelectorAll('[data-project-card]'));
      const newScales = cards.map(card => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.top + cardRect.height / 2;
        const dist = Math.abs(cardCenter - centerY);
        // Scale: 1.0 at center, down to 0.93 at farthest (tweak as needed)
        const maxDist = rect.height / 2;
        const t = Math.min(dist / maxDist, 1.0);
        return 1.0 + 0.09 * (1.0 - t); // 1.09 at center, 1.0 at edge
      });
      setCardScales(newScales);
    }
    const container = scrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      // Initial call
      handleScroll();
    }
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Add CSS to hide scrollbar for webkit browsers
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
      .mask-fade {
        -webkit-mask-image: linear-gradient(to bottom, transparent 0px, black 40px, black calc(100% - 40px), transparent 100%);
        mask-image: linear-gradient(to bottom, transparent 0px, black 40px, black calc(100% - 40px), transparent 100%);
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const renderContent = () => {
    switch (activeSection) {
      case "home":
        return (
          <div style={{ 
            padding: "2rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            height: "100%"
          }}>
            <p style={{ 
              color: "#bdbdbd", 
              lineHeight: 1.6, 
              fontSize: "0.9rem",
              fontWeight: 200,
              letterSpacing: "0.02em",
              textAlign: "left",
              maxWidth: "600px"
            }}>
              Harrison Chen is a planner, creator, analyst, and operator who helps bring ideas to life through thoughtful design and strategic execution. He expresses his creativity through food and is always searching for ways to merge his creative impulses with his strategic and analytical mindset.
            </p>
          </div>
        );
      case "projects":
        // Filter projects based on selected tags
        // If all tags are selected, show all projects (same as no filter)
        const filteredProjects = selectedTags.length > 0 && selectedTags.length < allTags.length
          ? projects.filter(project => selectedTags.includes(project.tag))
          : projects;
        
        return (
          <div style={{ padding: "2rem" }}>

            
            {selectedProject ? (
              // Detailed project view
              <div>
                <button
                  onClick={() => setSelectedProject(null)}
                  style={{
                    background: "none",
                    border: "none",
                    color: LINK,
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    marginBottom: "2rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem"
                  }}
                >
                  ← Back to Projects
                </button>
                <div style={{ 
                  border: `1px solid ${SUBTLE}`, 
                  padding: "2rem", 
                  borderRadius: "4px",
                  marginBottom: "2rem"
                }}>
                  <h2 style={{ color: ACCENT, fontSize: "1.8rem", fontWeight: 400, marginBottom: "1rem" }}>
                    {selectedProject.name}
            </h2>
                  <p style={{ color: "#bdbdbd", fontSize: "1rem", lineHeight: 1.6, marginBottom: "2rem" }}>
                    {selectedProject.details.overview}
                  </p>
                  
                  <div style={{ marginBottom: "2rem" }}>
                    <h3 style={{ color: ACCENT, fontSize: "1.2rem", marginBottom: "1rem" }}>
                      {selectedProject.details.skillsAndMethods ? "Skills & Methods" : "Technologies"}
                    </h3>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                      {(selectedProject.details.skillsAndMethods || selectedProject.details.technologies).map((item, index) => (
                        <span key={index} style={{
                          background: SUBTLE,
                          color: ACCENT,
                          padding: "0.3rem 0.8rem",
                          borderRadius: "20px",
                          fontSize: "0.8rem"
                        }}>
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div style={{ marginBottom: "2rem" }}>
                    <h3 style={{ color: ACCENT, fontSize: "1.2rem", marginBottom: "1rem" }}>
                      {selectedProject.details.highlights ? "Highlights" : "Key Features"}
                    </h3>
                    <ul style={{ color: "#bdbdbd", lineHeight: 1.6, paddingLeft: "1.5rem" }}>
                      {(selectedProject.details.highlights || selectedProject.details.features).map((item, index) => (
                        <li key={index} style={{ marginBottom: "0.5rem" }}>{item}</li>
                      ))}
                    </ul>
                  </div>

                  {selectedProject.details.businessImpact && (
                    <div style={{ marginBottom: "2rem" }}>
                      <h3 style={{ color: ACCENT, fontSize: "1.2rem", marginBottom: "1rem" }}>
                        Business Impact
                      </h3>
                      <ul style={{ color: "#bdbdbd", lineHeight: 1.6, paddingLeft: "1.5rem" }}>
                        {selectedProject.details.businessImpact.map((item, index) => (
                          <li key={index} style={{ marginBottom: "0.5rem" }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedProject.details.technicalHighlights && (
                    <div style={{ marginBottom: "2rem" }}>
                      <h3 style={{ color: ACCENT, fontSize: "1.2rem", marginBottom: "1rem" }}>
                        Technical Highlights
                      </h3>
                      <ul style={{ color: "#bdbdbd", lineHeight: 1.6, paddingLeft: "1.5rem" }}>
                        {selectedProject.details.technicalHighlights.map((item, index) => (
                          <li key={index} style={{ marginBottom: "0.5rem" }}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedProject.details.screenshots && (
                    <div style={{ marginBottom: "2rem" }} data-screenshots-section>
                      <h3 style={{ color: ACCENT, fontSize: "1.2rem", marginBottom: "1rem" }}>
                        System Screenshots
                      </h3>
                      <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                        {selectedProject.details.screenshots.map((screenshot, index) => (
                          <div key={index} style={{ 
                            border: `1px solid ${SUBTLE}`, 
                            borderRadius: "8px", 
                            overflow: "hidden",
                            background: "rgba(26,26,26,0.5)"
                          }}>
                            <img 
                              src={screenshot.url} 
                              alt={screenshot.title}
                              style={{ 
                                width: "100%", 
                                height: "auto", 
                                display: "block",
                                maxHeight: "300px",
                                objectFit: "cover"
                              }}
                            />
                            <div style={{ padding: "1rem" }}>
                              <h4 style={{ color: ACCENT, fontSize: "1rem", marginBottom: "0.5rem" }}>
                                {screenshot.title}
                              </h4>
                              <p style={{ color: "#bdbdbd", fontSize: "0.9rem", lineHeight: 1.5 }}>
                                {screenshot.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    {selectedProject.url && (
                      <a 
                        href={selectedProject.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-block",
                          background: LINK,
                          color: MAIN_BG,
                          padding: "0.8rem 1.5rem",
                          borderRadius: "4px",
                          textDecoration: "none",
                          fontWeight: 500,
                          transition: "background 0.2s"
                        }}
                        onMouseOver={e => e.currentTarget.style.background = "#5a9ee2"}
                        onMouseOut={e => e.currentTarget.style.background = LINK}
                      >
                        {selectedProject.url.includes('github.com') ? 'View on GitHub' : 'View Website'}
                      </a>
                    )}
                    {selectedProject.details.screenshots && (
                      <button
                        onClick={() => {
                          // Scroll to screenshots section
                          const screenshotsSection = document.querySelector('[data-screenshots-section]');
                          if (screenshotsSection) {
                            screenshotsSection.scrollIntoView({ behavior: 'smooth' });
                          }
                        }}
                        style={{
                          display: "inline-block",
                          background: "transparent",
                          color: LINK,
                          border: `1px solid ${LINK}`,
                          padding: "0.8rem 1.5rem",
                          borderRadius: "4px",
                          textDecoration: "none",
                          fontWeight: 500,
                          transition: "all 0.2s",
                          cursor: "pointer"
                        }}
                        onMouseOver={e => {
                          e.currentTarget.style.background = LINK;
                          e.currentTarget.style.color = MAIN_BG;
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color = LINK;
                        }}
                      >
                        View Screenshots
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Project list view
            <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                {filteredProjects.map((project, index) => {
                  const isHovered = hoveredCardIndex === index;
                  const scale = cardScales[index] || 1;
                  const hoverTransform = isHovered ? 'translateY(-2px)' : '';
                  return (
                    <div
                      key={index}
                      data-project-card
                      onClick={() => setSelectedProject(project)}
                      onMouseOver={() => setHoveredCardIndex(index)}
                      onMouseOut={() => setHoveredCardIndex(null)}
                      style={{
                        background: "rgba(26,26,26,0.75)",
                        border: `1.5px solid ${isHovered ? LINK : SUBTLE}`,
                        boxShadow: "0 2px 8px 0 rgba(0,0,0,0.10)",
                        padding: "1.5rem",
                        borderRadius: "8px",
                        transition: "all 0.2s cubic-bezier(.4,1.6,.4,1)",
                        cursor: "pointer",
                        pointerEvents: "auto",
                        transform: `scale(${scale}) ${hoverTransform}`.trim(),
                        willChange: "transform"
                      }}
                    >
                      <h3 style={{ color: ACCENT, fontSize: "1.2rem", marginBottom: "0.5rem" }}>
                        {project.name}
                      </h3>
                      <p style={{ color: "#bdbdbd", fontSize: "0.9rem" }}>
                        {project.desc}
                      </p>
                        <div style={{ 
                          marginTop: "1rem", 
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-end"
                        }}>
                          {/* Tag */}
                          <div style={{
                            background: SUBTLE,
                            color: ACCENT,
                            padding: "0.2rem 0.6rem",
                            borderRadius: "12px",
                            fontSize: "0.7rem",
                            fontWeight: 300,
                            letterSpacing: "0.02em",
                            textTransform: "uppercase"
                          }}>
                            {project.tag}
                          </div>
                          
                          {/* Arrows */}
                          <div style={{
                            display: "flex",
                            gap: "0.2rem",
                            alignItems: "center"
                          }}>
                            {[0, 1, 2].map((arrowIdx) => (
                              <div
                                key={arrowIdx}
                                data-arrow
                                style={{
                                  fontSize: "0.9rem",
                                  color: LINK,
                                  opacity: [0.6, 0.4, 0.2][arrowIdx],
                                  transform: hoveredCardIndex === index ? `translateX(${6 + arrowIdx * 2}px)` : "translateX(0)",
                                  transition: `transform 0.2s cubic-bezier(.4,1.6,.4,1) ${arrowIdx * 0.04}s`,
                                  willChange: "transform"
                                }}
                              >
                                →
                              </div>
                            ))}
                          </div>
                        </div>
                    </div>
                  );
                })}
            </div>
            )}
          </div>
        );
      case "info":
        return (
          <div style={{ padding: "2rem" }}>
            <div style={{ color: "#bdbdbd", lineHeight: 1.6 }}>
              <p style={{ marginBottom: "1rem" }}>
                <strong style={{ color: ACCENT }}>Location:</strong> Philadelphia, PA
              </p>
              <p style={{ marginBottom: "1rem" }}>
                <strong style={{ color: ACCENT }}>Expertise:</strong> Operations & Process Optimization, Web Development & Design, Culinary Innovation & Community Building, Project Management & Strategic Planning
              </p>
              <p style={{ marginBottom: "1rem" }}>
                <strong style={{ color: ACCENT }}>Currently:</strong> Available for freelance and collaboration opportunities
              </p>
            </div>
          </div>
        );
      case "contact":
        return (
          <div style={{ padding: "2rem" }}>
            <div style={{ color: "#bdbdbd", lineHeight: 1.6 }}>
              <p style={{ marginBottom: "1rem" }}>
                <strong style={{ color: ACCENT }}>Email:</strong>{" "}
                <a href="mailto:chenharrison97@gmail.com" style={{ color: LINK, textDecoration: "none", pointerEvents: "auto" }}>
                  chenharrison97@gmail.com
                </a>
              </p>
              <p style={{ marginBottom: "1rem" }}>
                <strong style={{ color: ACCENT }}>LinkedIn:</strong>{" "}
                <a href="https://www.linkedin.com/in/chenharrison97/" target="_blank" rel="noopener noreferrer" style={{ color: LINK, textDecoration: "none", pointerEvents: "auto" }}>
                  linkedin.com/in/chenharrison97
                </a>
              </p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: MAIN_BG,
        fontFamily: FONT_FAMILY,
        color: ACCENT,
        letterSpacing: "0.01em",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 0,
      }}
    >
     {/* Animated Shader Background */}
     <ShaderBackground />
     
      {/* Main Container with border, pushed in by 0.5in (48px) */}
      <div
        style={{
          width: `calc(100vw - 2 * ${BORDER_MARGIN})`,
          height: `calc(100vh - 2 * ${BORDER_MARGIN})`,
          maxWidth: 900,
          margin: `${BORDER_MARGIN} auto`,
          border: `2.5px solid ${BORDER}`,
          borderRadius: "0px",
          boxSizing: "border-box",
          boxShadow: "0 0 0 2.5px #222",
          // background: "rgba(26, 26, 26, 1.0)", // Removed to let shader show through
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          padding: 0,
          position: "relative",
          zIndex: 2,
          pointerEvents: "none", // Allow mouse events to pass through to canvas
        }}
        data-content-area
      >
        {/* Left Navigation Section */}
        <div style={{ 
          width: "250px", 
          height: "100%",
          padding: "1.5rem 0 0 2rem",
          pointerEvents: "none", // Allow mouse events to pass through to canvas
          position: "relative" // For submenu positioning
        }}>
          {/* Name in top left corner */}
          <div
            style={{
              position: "static",
              color: ACCENT,
              fontSize: "1.1rem",
              fontWeight: 300,
              fontFamily: FONT_FAMILY,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            Harrison Chen
            <div
              style={{
                fontSize: "0.75rem",
                fontWeight: 200,
                letterSpacing: "0.02em",
                marginTop: "0.3rem",
                color: "#bdbdbd",
                textTransform: "none",
              }}
            >
              Planner · Creator · Analyst · Operator
            </div>
            <div
              style={{
                marginTop: "3rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.8rem",
              }}
            >
              <a
                href="#"
                onClick={(e) => { 
                  e.preventDefault(); 
                  setActiveSection("home");
                  if (window.triggerRipple) {
                    const rect = e.currentTarget.querySelector('.nav-first-char').getBoundingClientRect();
                    const content = document.querySelector('[data-content-area]');
                    const contentRect = content.getBoundingClientRect();
                    const charX = (rect.left - contentRect.left) / contentRect.width;
                    const charY = 1.0 - (rect.top + rect.height / 2 - contentRect.top) / contentRect.height;
                    window.triggerRipple(charX, charY);
                  }
                }}
                style={{
                  color: activeSection === "home" ? LINK : ACCENT,
                  textDecoration: "none",
                  fontSize: "0.85rem",
                  fontWeight: 300,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  pointerEvents: "auto", // Re-enable clicks for this element
                }}
                onMouseOver={e => {
                  e.currentTarget.style.textShadow = "2px 2px 4px rgba(0,0,0,0.5)";
                  e.currentTarget.style.transform = "translateX(2px) skewX(-2deg)";
                }}
                onMouseOut={e => {
                  e.currentTarget.style.textShadow = "none";
                  e.currentTarget.style.transform = "none";
                }}
              >
                <span className="nav-first-char">H</span>ome
              </a>
              <a
                href="#"
                onClick={(e) => { 
                  e.preventDefault(); 
                  setActiveSection("projects");
                  if (window.triggerRipple) {
                    const rect = e.currentTarget.querySelector('.nav-first-char').getBoundingClientRect();
                    const content = document.querySelector('[data-content-area]');
                    const contentRect = content.getBoundingClientRect();
                    const charX = (rect.left - contentRect.left) / contentRect.width;
                    const charY = 1.0 - (rect.top + rect.height / 2 - contentRect.top) / contentRect.height;
                    window.triggerRipple(charX, charY);
                  }
                }}
                style={{
                  color: activeSection === "projects" ? LINK : ACCENT,
                  textDecoration: "none",
                  fontSize: "0.85rem",
                  fontWeight: 300,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  pointerEvents: "auto", // Re-enable clicks for this element
                }}
                onMouseOver={e => {
                  e.currentTarget.style.textShadow = "2px 2px 4px rgba(0,0,0,0.5)";
                  e.currentTarget.style.transform = "translateX(2px) skewX(-2deg)";
                }}
                onMouseOut={e => {
                  e.currentTarget.style.textShadow = "none";
                  e.currentTarget.style.transform = "none";
                }}
              >
                <span className="nav-first-char">P</span>rojects
              </a>
              
              {/* Inline Filter Buttons */}
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "0.3rem",
                pointerEvents: "auto",
                maxHeight: activeSection === "projects" ? "50px" : "0px",
                opacity: activeSection === "projects" ? 1 : 0,
                overflow: "hidden",
                transition: "all 0.3s ease-in-out",
                transform: activeSection === "projects" ? "translateY(0)" : "translateY(-5px)",
                marginTop: activeSection === "projects" ? "0.5rem" : "0px",
                marginBottom: activeSection === "projects" ? "0.8rem" : "0px",
                visibility: activeSection === "projects" ? "visible" : "hidden"
              }}>
                    <button
                      onClick={() => setSelectedTags([])}
                      style={{
                        background: (selectedTags.length === 0 || selectedTags.length === allTags.length) ? LINK : "transparent",
                        color: (selectedTags.length === 0 || selectedTags.length === allTags.length) ? MAIN_BG : ACCENT,
                        border: `1px solid ${(selectedTags.length === 0 || selectedTags.length === allTags.length) ? LINK : SUBTLE}`,
                        padding: "0.2rem 0.5rem",
                        borderRadius: "12px",
                        fontSize: "0.6rem",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        textTransform: "uppercase",
                        fontWeight: 300,
                        minWidth: "auto"
                      }}
                    >
                      All
                    </button>
                    {allTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => {
                          setSelectedTags(prev => {
                            // If all tags are currently selected, clicking any tag should select only that tag
                            if (prev.length === allTags.length) {
                              return [tag];
                            }
                            // Otherwise, toggle the tag as normal
                            return prev.includes(tag) 
                              ? prev.filter(t => t !== tag)
                              : [...prev, tag];
                          });
                        }}
                        style={{
                          background: (selectedTags.includes(tag) && selectedTags.length < allTags.length) ? LINK : "transparent",
                          color: (selectedTags.includes(tag) && selectedTags.length < allTags.length) ? MAIN_BG : ACCENT,
                          border: `1px solid ${(selectedTags.includes(tag) && selectedTags.length < allTags.length) ? LINK : SUBTLE}`,
                          padding: "0.2rem 0.5rem",
                          borderRadius: "12px",
                          fontSize: "0.6rem",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          textTransform: "uppercase",
                          fontWeight: 300,
                          minWidth: "auto"
                        }}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
              <a
                href="#"
                onClick={(e) => { 
                  e.preventDefault(); 
                  setActiveSection("info");
                  if (window.triggerRipple) {
                    const rect = e.currentTarget.querySelector('.nav-first-char').getBoundingClientRect();
                    const content = document.querySelector('[data-content-area]');
                    const contentRect = content.getBoundingClientRect();
                    const charX = (rect.left - contentRect.left) / contentRect.width;
                    const charY = 1.0 - (rect.top + rect.height / 2 - contentRect.top) / contentRect.height;
                    window.triggerRipple(charX, charY);
                  }
                }}
                style={{
                  color: activeSection === "info" ? LINK : ACCENT,
                  textDecoration: "none",
                  fontSize: "0.85rem",
                  fontWeight: 300,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  pointerEvents: "auto", // Re-enable clicks for this element
                }}
                onMouseOver={e => {
                  e.currentTarget.style.textShadow = "2px 2px 4px rgba(0,0,0,0.5)";
                  e.currentTarget.style.transform = "translateX(2px) skewX(-2deg)";
                }}
                onMouseOut={e => {
                  e.currentTarget.style.textShadow = "none";
                  e.currentTarget.style.transform = "none";
                }}
              >
                <span className="nav-first-char">I</span>nfo
              </a>
                              <a
                  href="#"
                  onClick={(e) => { 
                    e.preventDefault(); 
                    setActiveSection("contact");
                    if (window.triggerRipple) {
                      const rect = e.currentTarget.querySelector('.nav-first-char').getBoundingClientRect();
                      const content = document.querySelector('[data-content-area]');
                      const contentRect = content.getBoundingClientRect();
                      const charX = (rect.left - contentRect.left) / contentRect.width;
                      const charY = 1.0 - (rect.top + rect.height / 2 - contentRect.top) / contentRect.height;
                      window.triggerRipple(charX, charY);
                    }
                  }}
                style={{
                  color: activeSection === "contact" ? LINK : ACCENT,
                  textDecoration: "none",
                  fontSize: "0.85rem",
                  fontWeight: 300,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  pointerEvents: "auto", // Re-enable clicks for this element
                }}
                onMouseOver={e => {
                  e.currentTarget.style.textShadow = "2px 2px 4px rgba(0,0,0,0.5)";
                  e.currentTarget.style.transform = "translateX(2px) skewX(-2deg)";
                }}
                onMouseOut={e => {
                  e.currentTarget.style.textShadow = "none";
                  e.currentTarget.style.transform = "none";
                }}
              >
                <span className="nav-first-char">C</span>ontact
              </a>
            </div>
          </div>
        </div>
        
        {/* Right Content Section */}
        <div style={{ 
          flex: 1, 
          height: "100%",
          pointerEvents: "none", // Allow mouse events to pass through to canvas
          overflow: "hidden", // Prevent content from expanding the container
          position: "relative", // For fade overlays
          display: "flex",
          flexDirection: "column"
        }}>

          
          {/* Content Area with fade overlays */}
          <div style={{
            flex: 1,
            position: "relative",
            overflow: "hidden"
          }}>
            {/* Remove gradient overlay divs here */}
            <div 
              ref={scrollRef}
              className="hide-scrollbar mask-fade"
              style={{
                height: "100%", // Full height - no padding to subtract
                overflowY: "auto",
                overflowX: "hidden",
                pointerEvents: "auto", // Re-enable mouse events for scrolling
                scrollbarWidth: "none", // Firefox
                msOverflowStyle: "none", // IE/Edge
                position: "relative",
                zIndex: 1
              }}
            >
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
